import type { BadgeProps } from '@/components/ui/Badge';
import type { ItemStatus } from '@/features/inventory/hooks';

/**
 * Lifecycle labels for the item master. The stored value `archived`
 * used to be `discontinued` (migration 130 renamed the enum).
 */
export const ITEM_STATUS_META: Record<
  ItemStatus,
  { label: string; variant: NonNullable<BadgeProps['variant']> }
> = {
  needs_verification: { label: 'Needs verification', variant: 'warning' },
  concept: { label: 'Concept', variant: 'muted' },
  in_development: { label: 'In development', variant: 'info' },
  pre_production: { label: 'Pre-production', variant: 'info' },
  active: { label: 'Active', variant: 'success' },
  on_hold: { label: 'On hold', variant: 'warning' },
  archived: { label: 'Archived', variant: 'muted' },
};

/**
 * Badge copy for a status read from the database. A value this build
 * doesn't list (e.g. `discontinued` on a database that hasn't run
 * migration 130 yet) gets a muted badge instead of crashing the page.
 */
export function getItemStatusMeta(status: string): {
  label: string;
  variant: NonNullable<BadgeProps['variant']>;
} {
  const known: (typeof ITEM_STATUS_META)[ItemStatus] | undefined =
    ITEM_STATUS_META[status as ItemStatus];
  if (known) return known;
  const words = status.replace(/_/g, ' ').trim();
  return {
    label: words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Unknown',
    variant: 'muted',
  };
}

export const ITEM_STATUS_OPTIONS = (Object.keys(ITEM_STATUS_META) as ItemStatus[]).map(
  (status) => ({
    value: status,
    label: ITEM_STATUS_META[status].label,
  }),
);

/** Archived items stay in the catalog for history, but they are not operational. */
export function isArchivedStatus(status: string | null | undefined): boolean {
  return status === 'archived';
}
